import { useEffect, useRef, useState, useMemo } from 'react';
import { useEditor } from './EditorContext';
import { EmojiPicker } from '../components/EmojiPicker';
import { RichTextEditor } from './RichTextEditor';
import { RecurrenceModal } from './RecurrenceModal';
import { api } from '../api/client';
import {
  recurrenceKind,
  monthlyOptionLabel,
  describeRecurrence,
  setRecurrenceKind,
  setRecurrence,
  occurrenceOverride,
  itemAppliesOn,
  includeOccurrence,
  clearOccurrenceOverride,
  type RecurrenceKind,
} from '../util/recurrence';
import { useTenant } from '../auth/TenantProvider';
import {
  getItemTypeLabel,
  getValidItemTypes,
  type Attachment,
  type Item,
  type Subitem,
  type TimeRange,
} from '../types/agenda';
import './item-detail.css';

export function ItemDetailPanel({ index }: { index: number }) {
  const { draft, updateItem, updateSubitems, justAddedItemId, clearJustAdded } = useEditor();
  const { activeTenant } = useTenant();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [recurrenceModalOpen, setRecurrenceModalOpen] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const item = draft.items[index];

  useEffect(() => {
    if (item && justAddedItemId === item.id) {
      titleRef.current?.select();
      clearJustAdded();
    }
  }, [item?.id, justAddedItemId, clearJustAdded]);

  if (!item) return null;

  const meetingTypeConfig = useMemo(() => {
    if (!activeTenant) return undefined;
    return activeTenant.meetingTypes.find((mt) => mt.key === draft.meetingType);
  }, [draft.meetingType, activeTenant]);

  const types = meetingTypeConfig ? getValidItemTypes(meetingTypeConfig) : [];
  const isGeneral = draft.meetingType === 'general'; // Assumes 'general' supports full timing

  const patch = (p: Partial<Item>) => updateItem(index, { ...item, ...p });

  // Build a TimeRange from the start/duration/independent inputs.
  const patchTiming = (next: { start?: string; duration?: string; independent?: boolean }) => {
    const start = next.start ?? item.timeRange?.start ?? '';
    const durStr = next.duration ?? (item.timeRange?.duration ? String(item.timeRange.duration) : '');
    const independent = next.independent ?? item.timeRange?.independent ?? false;
    const duration = Number.parseInt(durStr, 10) || 0;

    let tr: TimeRange | undefined;
    if (isGeneral) {
      tr = { start: start || undefined, duration: duration || undefined, independent: independent || undefined };
      if (!tr.start && !tr.duration && !tr.independent) tr = undefined;
    } else {
      tr = duration > 0 ? { duration } : undefined;
    }
    patch({ timeRange: tr });
  };

  return (
    <div className="item-detail">
      <label className="field">
        <span className="field-label">Type</span>
        <div className="segmented">
          {types.map((t) => (
            <button
              key={t}
              className={`segment${item.type === t ? ' active' : ''}`}
              onClick={() => patch({ type: t })}
            >
              {getItemTypeLabel(t, meetingTypeConfig)}
            </button>
          ))}
        </div>
      </label>

      <div className="field">
        <span className="field-label">Emoji</span>
        <div className="field-row">
          <input
            className="input emoji-input"
            value={item.emoji ?? ''}
            onChange={(e) => patch({ emoji: e.target.value })}
            placeholder="e.g. 🎤 👋"
          />
          <button className="btn" style={{ flex: 'none' }} onClick={() => setPickerOpen(true)}>
            Pick
          </button>
        </div>
      </div>
      {pickerOpen && (
        <EmojiPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onPick={(emoji) => {
            const cur = (item.emoji ?? '').trim();
            patch({ emoji: cur ? `${cur} ${emoji}` : emoji });
          }}
        />
      )}

      <label className="field">
        <span className="field-label">Title</span>
        <input
          ref={titleRef}
          className="input"
          value={item.title}
          onChange={(e) => patch({ title: e.target.value })}
          placeholder="Item title"
        />
      </label>

      <label className="field">
        <span className="field-label">Leader</span>
        <input
          className="input"
          value={item.leader ?? ''}
          onChange={(e) => patch({ leader: e.target.value })}
          placeholder="Facilitator name"
        />
      </label>

      <label className="field">
        <span className="field-label">Recurrence</span>
        <select
          className="input"
          value={recurrenceKind(item)}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '__custom__') {
              setRecurrenceModalOpen(true);
              return;
            }
            updateItem(index, setRecurrenceKind(item, v as RecurrenceKind, draft.date));
          }}
        >
          <option value="none">Does not repeat</option>
          <option value="every">Every meeting</option>
          <option value="monthly">{monthlyOptionLabel(item, draft.date)}</option>
          {recurrenceKind(item) === 'custom' && (
            <option value="custom">{describeRecurrence(item.recurrence)}</option>
          )}
          <option value="__custom__">
            {recurrenceKind(item) === 'custom' ? 'Edit custom schedule…' : 'Custom…'}
          </option>
        </select>
        <small className="field-hint">Recurring items carry into the next agenda of this type.</small>
        <OccurrenceStatus
          item={item}
          agendaDate={draft.date}
          onChange={(next) => updateItem(index, next)}
        />
      </label>

      <RecurrenceModal
        open={recurrenceModalOpen}
        recurrence={item.recurrence}
        agendaDate={draft.date}
        onSave={(rec) => updateItem(index, setRecurrence(item, rec, draft.date))}
        onClose={() => setRecurrenceModalOpen(false)}
      />

      {isGeneral ? (
        <div className="field">
          <span className="field-label">Timing</span>
          <div className="field-row">
            <label className="field">
              <span className="field-sublabel">Start (H:MM)</span>
              <input
                className="input"
                inputMode="numeric"
                value={item.timeRange?.start ?? ''}
                onChange={(e) => patchTiming({ start: e.target.value })}
                placeholder="7:00"
              />
            </label>
            <label className="field">
              <span className="field-sublabel">Duration (min)</span>
              <input
                className="input"
                inputMode="numeric"
                value={item.timeRange?.duration ? String(item.timeRange.duration) : ''}
                onChange={(e) => patchTiming({ duration: e.target.value })}
                placeholder="10"
              />
            </label>
          </div>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={item.timeRange?.independent ?? false}
              onChange={(e) => patchTiming({ independent: e.target.checked })}
            />
            <span className="checkbox-text">
              Independent start
              <small>Fixed time, not chained to the previous item</small>
            </span>
          </label>
        </div>
      ) : (
        <label className="field" style={{ maxInlineSize: 180 }}>
          <span className="field-label">Duration (min)</span>
          <input
            className="input"
            inputMode="numeric"
            value={item.timeRange?.duration ? String(item.timeRange.duration) : ''}
            onChange={(e) => patchTiming({ duration: e.target.value })}
            placeholder="10"
          />
        </label>
      )}

      <div className="field">
        <span className="field-label">Description</span>
        <RichTextEditor
          value={item.body ?? ''}
          onChange={(body) => patch({ body })}
          placeholder="Optional details."
        />
      </div>

      <AttachmentsEditor
        agendaId={draft.id}
        attachments={item.attachments ?? []}
        onChange={(atts) => patch({ attachments: atts.length ? atts : undefined })}
      />

      <SubitemsEditor
        subitems={item.subitems ?? []}
        onChange={(subs) => updateSubitems(index, subs)}
      />
    </div>
  );
}

// Per-occurrence override control for a recurring item: skip/restore/include for
// this specific meeting, independent of the recurrence schedule.
function OccurrenceStatus({
  item,
  agendaDate,
  onChange,
}: {
  item: Item;
  agendaDate: string;
  onChange: (next: Item) => void;
}) {
  if (!item.recurring) return null;
  const override = occurrenceOverride(item, agendaDate);
  // Whether the schedule alone (ignoring per-occurrence overrides) matches.
  const scheduled = itemAppliesOn(
    { ...item, recurrence: item.recurrence && { ...item.recurrence, overrides: undefined } },
    agendaDate,
  );

  let text: string;
  let action: { label: string; next: Item };
  if (override === false) {
    text = 'Skipped for this meeting.';
    action = { label: 'Restore', next: clearOccurrenceOverride(item, agendaDate) };
  } else if (override === true && !scheduled) {
    text = 'Included for this meeting (off schedule).';
    action = { label: 'Remove', next: clearOccurrenceOverride(item, agendaDate) };
  } else if (override === undefined && !scheduled) {
    text = 'Not scheduled for this meeting.';
    action = { label: 'Include', next: includeOccurrence(item, agendaDate) };
  } else {
    return null; // applies normally — nothing to override
  }

  return (
    <div className="recurrence-skip-notice">
      <span>{text}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={() => onChange(action.next)}>
        {action.label}
      </button>
    </div>
  );
}

function AttachmentsEditor({
  agendaId,
  attachments,
  onChange,
}: {
  agendaId: string;
  attachments: Attachment[];
  onChange: (atts: Attachment[]) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftType, setDraftType] = useState<'text/plain' | 'text/html'>('text/plain');
  const [draftText, setDraftText] = useState('');

  const addDescriptors = (...next: Attachment[]) => onChange([...attachments, ...next]);

  const upload = async (file: File | Blob, filename?: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.uploadAttachment(agendaId, file, filename);
      addDescriptors({
        hash: r.hash,
        filename: r.filename,
        contentType: r.contentType,
        size: r.size,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const onPickFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // allow re-selecting the same file
    for (const f of files) await upload(f);
  };

  const saveContent = async () => {
    const ext = draftType === 'text/html' ? '.html' : '.txt';
    let name = draftName.trim() || 'note';
    if (!/\.[a-z0-9]+$/i.test(name)) name += ext;
    await upload(new Blob([draftText], { type: draftType }), name);
    setComposing(false);
    setDraftName('');
    setDraftText('');
    setDraftType('text/plain');
  };

  const remove = (i: number) => {
    const next = attachments.slice();
    next.splice(i, 1);
    onChange(next);
  };

  return (
    <div className="field attachments">
      <span className="field-label">Attachments</span>

      {attachments.length > 0 && (
        <ul className="attachment-list">
          {attachments.map((at, i) => (
            <li key={`${at.hash}-${i}`} className="attachment-item">
              <a
                className="attachment-name"
                href={`/files/${at.hash}/${encodeURIComponent(at.filename)}`}
                target="_blank"
                rel="noreferrer"
                title={at.filename}
              >
                {at.title || at.filename}
              </a>
              <span className="attachment-type">{at.contentType}</span>
              <button
                type="button"
                className="attachment-remove"
                onClick={() => remove(i)}
                aria-label={`Remove ${at.filename}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="attachment-actions">
        <button type="button" disabled={busy} onClick={() => fileInputRef.current?.click()}>
          Add file
        </button>
        <button type="button" disabled={busy} onClick={() => setComposing((v) => !v)}>
          {composing ? 'Cancel content' : 'Add content'}
        </button>
        {busy && <span className="attachment-status">Uploading…</span>}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        accept=".pdf,.html,.htm,.txt,application/pdf,text/html,text/plain"
        onChange={onPickFiles}
      />

      {composing && (
        <div className="attachment-compose">
          <div className="field-row">
            <label className="field">
              <span className="field-sublabel">Filename</span>
              <input
                className="input"
                value={draftName}
                placeholder="note"
                onChange={(e) => setDraftName(e.target.value)}
              />
            </label>
            <label className="field" style={{ maxInlineSize: 140 }}>
              <span className="field-sublabel">Type</span>
              <select
                className="input"
                value={draftType}
                onChange={(e) => setDraftType(e.target.value as 'text/plain' | 'text/html')}
              >
                <option value="text/plain">Plain text</option>
                <option value="text/html">HTML</option>
              </select>
            </label>
          </div>
          <textarea
            className="input attachment-textarea"
            rows={6}
            value={draftText}
            placeholder="Paste or type content to attach as a file…"
            onChange={(e) => setDraftText(e.target.value)}
          />
          <div className="attachment-actions">
            <button type="button" disabled={busy || !draftText.trim()} onClick={saveContent}>
              Save content
            </button>
          </div>
        </div>
      )}

      {error && <span className="attachment-error">{error}</span>}
    </div>
  );
}

function SubitemsEditor({
  subitems,
  onChange,
}: {
  subitems: Subitem[];
  onChange: (subs: Subitem[]) => void;
}) {
  const [openPickers, setOpenPickers] = useState<Record<number, boolean>>({});

  const update = (i: number, patch: Partial<Subitem>) => {
    const next = subitems.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i: number) => {
    const next = subitems.slice();
    next.splice(i, 1);
    onChange(next);
    // Clean up picker state for removed index
    const newPickers = { ...openPickers };
    delete newPickers[i];
    setOpenPickers(newPickers);
  };
  const add = () => onChange([...subitems, { title: 'New sub-item' }]);

  const togglePickerOpen = (i: number) => {
    setOpenPickers((prev) => ({
      ...prev,
      [i]: !prev[i],
    }));
  };
  const closePickerAt = (i: number) => {
    setOpenPickers((prev) => ({
      ...prev,
      [i]: false,
    }));
  };
  const pickEmojiAt = (i: number, emoji: string) => {
    const cur = (subitems[i]?.emoji ?? '').trim();
    update(i, { emoji: cur ? `${cur} ${emoji}` : emoji });
  };

  return (
    <div className="field">
      <span className="field-label">Sub-items ({subitems.length})</span>
      <div className="subitems">
        {subitems.map((sub, i) => (
          <div className="subitem-card" key={i}>
            <div className="subitem-header">
              <button className="item-del" aria-label="Remove sub-item" onClick={() => remove(i)}>
                ✕
              </button>
            </div>

            <div className="field">
              <span className="field-sublabel">Emoji</span>
              <div className="field-row">
                <input
                  className="input emoji-input"
                  value={sub.emoji ?? ''}
                  onChange={(e) => update(i, { emoji: e.target.value })}
                  placeholder="e.g. 🎤 👋"
                />
                <button className="btn" style={{ flex: 'none' }} onClick={() => togglePickerOpen(i)}>
                  Pick
                </button>
              </div>
            </div>
            {(openPickers[i] ?? false) && (
              <EmojiPicker
                open={openPickers[i] ?? false}
                onClose={() => closePickerAt(i)}
                onPick={(emoji) => {
                  pickEmojiAt(i, emoji);
                  closePickerAt(i);
                }}
              />
            )}

            <label className="field">
              <span className="field-sublabel">Title</span>
              <input
                className="input"
                value={sub.title}
                onChange={(e) => update(i, { title: e.target.value })}
                placeholder="Sub-item title"
                aria-label="Sub-item title"
              />
            </label>

            <label className="field">
              <span className="field-sublabel">Leader</span>
              <input
                className="input"
                value={sub.leader ?? ''}
                onChange={(e) => update(i, { leader: e.target.value })}
                placeholder="Facilitator name"
                aria-label="Sub-item leader"
              />
            </label>

            <div className="field">
              <span className="field-sublabel">Description</span>
              <RichTextEditor
                value={sub.body ?? ''}
                onChange={(body) => update(i, { body })}
                placeholder="Optional details."
              />
            </div>
          </div>
        ))}
      </div>
      <button className="btn btn-block" onClick={add} style={{ marginBlockStart: 8 }}>
        + Add Sub-item
      </button>
    </div>
  );
}
